import { Toast } from '@byecode/ui'
import { type ActorEvent, DELAY_TYPE } from '@lighthouse/core'
import { useCallback, useEffect, useRef } from 'react'

import { DelayRunController } from '../utils/delayRun'
import { useLoadings } from './useLoadings'

/** Shared across hook instances so one view RECORD_CLICK locks every row of that view. */
const runningTriggerIds = new Set<string>()

function getRunningKey(id: string, actorEvent: ActorEvent) {
    return `${actorEvent.type}:${id}`
}

export const useActionRunningLoadings = () => {
    const { loadings, startLoading, stopLoading } = useLoadings()
    const delayRunnerRef = useRef<DelayRunController>(new DelayRunController())

    useEffect(() => {
        const runner = delayRunnerRef.current
        return () => {
            runner.dispose()
        }
    }, [])

    const handleRunActionWithLoading = useCallback(
        async (params: { id: string; actorEvent: ActorEvent; trigger?: (id: string, actorEvent: ActorEvent) => Promise<void> }) => {
            const { id, actorEvent, trigger } = params
            const { actions } = actorEvent
            if (!actions || actions.length === 0) {
                return
            }

            const runningKey = getRunningKey(id, actorEvent)
            if (runningTriggerIds.has(runningKey)) {
                return
            }
            runningTriggerIds.add(runningKey)

            startLoading(id, false)
            try {
                await trigger?.(id, actorEvent)
            } catch {
                Toast.error('执行失败')
            } finally {
                runningTriggerIds.delete(runningKey)
                stopLoading(id)
            }
        },
        [startLoading, stopLoading]
    )

    const handleActionTriggerWithLoading = useCallback(
        (params: { id: string; actorEvent: ActorEvent | undefined; trigger?: (id: string, actorEvent: ActorEvent) => Promise<void> }) => {
            const { id, actorEvent, trigger } = params
            if (!actorEvent) {
                return
            }
            const { config } = actorEvent
            const delayType = config?.delayRunConfig?.type
            const delayTime = config?.delayRunConfig?.time ?? 0

            // keep legacy behavior: delayType missing behaves like NONE (run immediately)
            const normalizedType = delayType ?? DELAY_TYPE.NONE
            return delayRunnerRef.current.run(getRunningKey(id, actorEvent), normalizedType, delayTime, () =>
                handleRunActionWithLoading({ id, actorEvent, trigger })
            )
        },
        [handleRunActionWithLoading]
    )

    return { loadings, startLoading, stopLoading, handleActionTriggerWithLoading }
}
